/**
 * Create Community Use Case
 * Business logic for creating a new community
 */

import type { CommunitiesRepository } from '../data/communities-repository';
import type { InsertCommunityRequest, InsertCommunityResponse } from '../types/communities-types';

export class CreateCommunityUseCase {
  constructor(private readonly repository: CommunitiesRepository) {}

  /**
   * Execute use case to create a new community
   * @param data - Community creation data
   */
  async execute(data: InsertCommunityRequest): Promise<InsertCommunityResponse> {
    try {
      // Validate required fields
      if (!data.profileName || data.profileName.trim() === '') {
        throw new Error('Profile name is required');
      }

      if (data.strategyType === undefined || ![0, 1, 2].includes(data.strategyType)) {
        throw new Error('Valid strategy type is required (0=EnergyPercentage, 1=ByKwh, 2=TotalPercentage)');
      }

      if (!data.startDate) {
        throw new Error('Start date is required');
      }

      // Validate date format (ISO format YYYY-MM-DD)
      const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
      if (!dateRegex.test(data.startDate)) {
        throw new Error('Start date must be in YYYY-MM-DD format');
      }

      if (data.expirationDate && !dateRegex.test(data.expirationDate)) {
        throw new Error('Expiration date must be in YYYY-MM-DD format');
      }

      // Validate expiration date is after start date
      if (data.expirationDate) {
        const start = new Date(data.startDate);
        const expiry = new Date(data.expirationDate);

        if (expiry <= start) {
          throw new Error('Expiration date must be after start date');
        }
      }

      const response = await this.repository.createCommunity(data);

      if (!response.success) {
        throw new Error(response.message || 'Failed to create community');
      }

      return response;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to create community';
      throw new Error(message);
    }
  }
}
